import React, { useReducer } from "react";
import SpotReviewContext from "./spotReviewContext";
import spotReviewReducer from "./spotReviewReducer";
import { API_BASE, apiErrorMessage, authedFetch } from "../../utils/api";

import {
  ADD_SPOT_REVIEW_COMPLETE,
  GET_SPOT_REVIEWS,
  GET_SPOT_REVIEWS_ERROR,
  ADD_SPOT_REVIEW,
  ADD_SPOT_REVIEW_ERROR,
  UPDATE_SPOT_REVIEW,
  UPDATE_SPOT_REVIEW_ERROR,
  DELETE_SPOT_REVIEW,
  DELETE_SPOT_REVIEW_ERROR,
  DELETE_SPOT_REVIEW_PHOTO,
  DELETE_SPOT_REVIEW_PHOTO_ERROR,
  GET_SPOTS,
  GET_SPOTS_ERROR,
  ADD_DRAFT_SPOT_REVIEW,
  REMOVE_DRAFT_SPOT_REVIEW,
  ADD_DRAFT_SUB_SPOT_REVIEW,
  REMOVE_DRAFT_SUB_SPOT_REVIEW,
  GET_USER_REVIEWS,
  GET_USER_REVIEWS_ERROR,
  GET_SPOT_REVIEW,
  GET_SPOT_REVIEW_ERROR,
} from "../types";

const SpotReviewState = (props) => {
  const initialState = {
    spots: [],
    spotsTotal: 0,
    spotsPage: 1,
    spotsPageSize: 6,
    spotsHasMore: false,
    spotReviews: [],
    userReviews: [],
    userReviewsTotal: 0,
    userReviewsPage: 1,
    userReviewsPageSize: 6,
    userReviewsHasMore: false,
    draftSpotReviews: [],
    loading: true,
    spotReviewAdded: false,
    message: null,
  };

  const [state, dispatch] = useReducer(spotReviewReducer, initialState);

  const getSpots = async (page = 1, pageSize = 6, append = false) => {
    const url = `${API_BASE}/spots?page=${page}&page_size=${pageSize}`;

    const res = await authedFetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });
    const res_json = await res.json();
    if (res.ok) {
      dispatch({
        type: GET_SPOTS,
        payload: {
          items: res_json.items || [],
          total: res_json.total || 0,
          page: res_json.page || page,
          pageSize: res_json.page_size || pageSize,
          hasMore: !!res_json.has_more,
          append,
        },
      });
    } else {
      const error_msg = res.status === 401 ? "Unauthorized" : apiErrorMessage(res_json);
      dispatch({ type: GET_SPOTS_ERROR, payload: error_msg });
    }
  }

  const getSpotReviewsForId = async (id) => {
    var url = `${API_BASE}/spot/` + id;
    const res = await authedFetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });
    const res_json = await res.json();
    if (res.ok) {
      dispatch({ type: GET_SPOT_REVIEWS, payload: res_json });
    } else {
      var error_msg = null;
      if (res.status === 401) {
        error_msg = "Unauthorized";
      } else {
        error_msg = apiErrorMessage(res_json);
      }
      dispatch({ type: GET_SPOT_REVIEWS_ERROR, payload: error_msg });
    }
  };

  const getUserReviews = async (page = 1, pageSize = 6, append = false) => {
    const url = `${API_BASE}/user/reviews?page=${page}&page_size=${pageSize}`;
    const res = await authedFetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });
    const res_json = await res.json();
    if (res.ok) {
      dispatch({
        type: GET_USER_REVIEWS,
        payload: {
          items: res_json.items || [],
          total: res_json.total || 0,
          page: res_json.page || page,
          pageSize: res_json.page_size || pageSize,
          hasMore: !!res_json.has_more,
          append,
        },
      });
    } else {
      const error_msg = res.status === 401 ? "Unauthorized" : apiErrorMessage(res_json);
      dispatch({ type: GET_USER_REVIEWS_ERROR, payload: error_msg });
    }
  };

  // Fetch the sub-reviews of a given parent review. Returns the list on
  // success (or [] on failure). The review detail page keeps these in local
  // state rather than global reducer state — they are only shown in one
  // place and are cheap to refetch on navigation.
  const getSubReviews = async (review_id) => {
    try {
      const res = await authedFetch(`${API_BASE}/spot/review/${review_id}/sub-reviews`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });
      if (!res.ok) return [];
      return await res.json();
    } catch (e) {
      return [];
    }
  };

  const getSpotReview = async (review_id) => {
    const res = await authedFetch(`${API_BASE}/spot/review/${review_id}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });
    const res_json = await res.json();
    if (res.ok) {
      dispatch({ type: GET_SPOT_REVIEW, payload: res_json });
    } else {
      const error_msg = res.status === 401 ? "Unauthorized" : apiErrorMessage(res_json);
      dispatch({ type: GET_SPOT_REVIEW_ERROR, payload: error_msg });
    }
  };

  const addSpotReview = async (spotReview) => {
    const formData = new FormData();
    formData.append("title", spotReview.title);
    formData.append("content", spotReview.content);
    formData.append("placeId", spotReview.placeId);
    formData.append("photoNum", (spotReview.photos || []).length);
    if (spotReview.tripOnly) {
      formData.append("tripOnly", "true");
    }
    if (spotReview.visitStartTime) {
      const startUnix = Math.floor(new Date(spotReview.visitStartTime).getTime() / 1000);
      formData.append("visitFrom", String(startUnix));
      if (spotReview.visitDuration) {
        const endUnix = startUnix + parseInt(spotReview.visitDuration) * 60;
        formData.append("visitTo", String(endUnix));
      }
    }
    if (spotReview.price) {
      formData.append("price", spotReview.price);
    }
    if (spotReview.category) {
      formData.append("category", spotReview.category);
    }
    if (spotReview.placeTypes && spotReview.placeTypes.length > 0) {
      formData.append("placeTypes", JSON.stringify(spotReview.placeTypes));
    }
    // Sub-review: when the composer is opened from a parent's detail page.
    if (spotReview.parentId && spotReview.relation) {
      formData.append("parentId", spotReview.parentId);
      formData.append("relation", spotReview.relation);
    }
    // A dirty way to support multiple files upload. Need to figure out multiple photos upload.
    for (let i = 0; i < (spotReview.photos || []).length; i++) {
      formData.append("photo-" + i, spotReview.photos[i]);
    }

    const res = await authedFetch(`${API_BASE}/spot/review`, {
      method: "POST",
      body: formData,
    });
    const res_json = await res.json();
    if (res.ok) {
      dispatch({ type: ADD_SPOT_REVIEW, payload: res_json });
      // Return the saved review so callers that need to thread the id
      // (e.g. the standalone-preview step posting subs after the parent)
      // can await it. Existing callers ignore the return value, so this
      // is backwards-compatible.
      return res_json;
    } else {
      dispatch({ type: ADD_SPOT_REVIEW_ERROR, payload: apiErrorMessage(res_json) });
      return null;
    }
  };

  const updateSpotReview = async (review_id, fields) => {
    const formData = new FormData();
    if (fields.title != null) formData.append("title", fields.title);
    if (fields.content != null) formData.append("content", fields.content);
    if (fields.price != null) formData.append("price", fields.price);
    if (fields.visitStartTime) formData.append("visitStartTime", fields.visitStartTime);
    if (fields.visitDuration) formData.append("visitDuration", fields.visitDuration);
    if (fields.category) formData.append("category", fields.category);
    if (fields.promote) formData.append("promote", "true");
    // New photo uploads
    if (fields.photos && fields.photos.length > 0) {
      formData.append("photoNum", fields.photos.length);
      for (let i = 0; i < fields.photos.length; i++) {
        formData.append("photo-" + i, fields.photos[i]);
      }
    }

    const res = await authedFetch(`${API_BASE}/spot/review/${review_id}`, {
      method: "PUT",
      body: formData,
    });
    const res_json = await res.json();
    if (res.ok) {
      dispatch({ type: UPDATE_SPOT_REVIEW, payload: res_json });
      return { ok: true, data: res_json };
    } else {
      dispatch({ type: UPDATE_SPOT_REVIEW_ERROR, payload: apiErrorMessage(res_json) });
      return { ok: false, msg: apiErrorMessage(res_json) };
    }
  };

  const deleteSpotReviewPhoto = async (review_id, photo_id) => {
    const res = await authedFetch(`${API_BASE}/spot/review/${review_id}/photo/${photo_id}`, {
      method: "DELETE",
    });
    if (res.ok) {
      dispatch({ type: DELETE_SPOT_REVIEW_PHOTO, payload: { review_id, photo_id } });
      return { ok: true };
    }
    let error_msg = "Failed to delete photo";
    try {
      const res_json = await res.json();
      error_msg = apiErrorMessage(res_json) || error_msg;
    } catch (e) {}
    dispatch({ type: DELETE_SPOT_REVIEW_PHOTO_ERROR, payload: error_msg });
    return { ok: false, msg: error_msg };
  };

  const deleteSpotReview = async (review_id) => {
    const res = await authedFetch(`${API_BASE}/spot/review/` + review_id, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
    });
    if (res.ok) {
      dispatch({ type: DELETE_SPOT_REVIEW, payload: review_id });
      return { ok: true };
    }
    let error_msg = "Failed to delete spot review";
    try {
      const res_json = await res.json();
      error_msg = res.status === 401 ? "Unauthorized" : apiErrorMessage(res_json) || error_msg;
    } catch (e) {}
    dispatch({ type: DELETE_SPOT_REVIEW_ERROR, payload: error_msg });
    return { ok: false, msg: error_msg };
  };

  const addDraftSpotReview  = async (draft) => {
    dispatch({ type: ADD_DRAFT_SPOT_REVIEW, payload: draft });
  };

  const removeDraftSpotReview = (index) => {
    dispatch({ type: REMOVE_DRAFT_SPOT_REVIEW, payload: index });
  };

  // Attach a sub-review draft to a parent draft already in draftSpotReviews.
  // The sub-review lives inline on the parent (parent.subReviews) so it can
  // be submitted together when the trip is saved. `relation` identifies the
  // role ("parking" | "viewpoint" | "trailhead").
  const addDraftSubSpotReview = (parentIndex, subReview) => {
    dispatch({
      type: ADD_DRAFT_SUB_SPOT_REVIEW,
      payload: { parentIndex, subReview },
    });
  };

  const removeDraftSubSpotReview = (parentIndex, subIndex) => {
    dispatch({
      type: REMOVE_DRAFT_SUB_SPOT_REVIEW,
      payload: { parentIndex, subIndex },
    });
  };

  const addSpotReviewComplete = () => {
    dispatch({ type: ADD_SPOT_REVIEW_COMPLETE });
  };

  return (
    <SpotReviewContext.Provider
      value={{
        spots: state.spots,
        spotsTotal: state.spotsTotal,
        spotsPage: state.spotsPage,
        spotsPageSize: state.spotsPageSize,
        spotsHasMore: state.spotsHasMore,
        spotReviews: state.spotReviews,
        userReviews: state.userReviews,
        userReviewsTotal: state.userReviewsTotal,
        userReviewsPage: state.userReviewsPage,
        userReviewsPageSize: state.userReviewsPageSize,
        userReviewsHasMore: state.userReviewsHasMore,
        draftSpotReviews: state.draftSpotReviews,
        loading: state.loading,
        spotReviewAdded: state.spotReviewAdded,
        message: state.message,
        getSpots,
        getSpotReviewsForId,
        getSpotReview,
        getSubReviews,
        getUserReviews,
        addSpotReview,
        addSpotReviewComplete,
        addDraftSpotReview,
        removeDraftSpotReview,
        addDraftSubSpotReview,
        removeDraftSubSpotReview,
        updateSpotReview,
        deleteSpotReviewPhoto,
        deleteSpotReview,
      }}
    >
      {props.children}
    </SpotReviewContext.Provider>
  );
};

export default SpotReviewState;
